import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, User, Lock, Mail, X, Eye, EyeOff } from 'lucide-react';
import Navbar from './Navbar';

const API_BASE_URL = 'https://api.4fingerstudios.com';

const Input = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-red-400/60" size={20} />
    <input
      {...props}
      className={`w-full bg-black border border-red-500/20 pl-10 p-4 text-white placeholder-red-400/50 focus:border-red-500/40 focus:outline-none transition-all duration-300 rounded-lg ${className}`}
    />
  </div>
);

const PasswordInput = ({ icon = Lock, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} icon={icon} type={show ? 'text' : 'password'} />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors duration-300"
        onClick={() => setShow(prev => !prev)}
      >
        {show ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
};

const ComicsPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [formState, setFormState] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          // User authenticated
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
      }
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedbackMessage({ text: '', type: '' });

    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      const clientIp = ipData.ip;

      let response;
      let responseText;

      if (formState === 'signup') {
        if (!username || !email || !password) {
          throw new Error('Please fill in all required fields.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password, email })
        });

        responseText = await response.text();

        if (!response.ok) {
          throw new Error(responseText || 'Signup failed');
        }

        setFeedbackMessage({ text: responseText || 'Account created! Please verify your email.', type: 'success' });
        setTimeout(() => {
          setShowAuthModal(false);
          setFormState('login');
          setUsername('');
          setEmail('');
          setPassword('');
          // Navigate to verify email if route exists
          if (navigate) {
            navigate('/verify-email', { state: { username } });
          }
        }, 2000);
      } else if (formState === 'login') {
        if (!username || !password) {
          throw new Error('Please enter both username and password.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password })
        });

        responseText = await response.text();

        if (!response.ok) {
          if (response.status === 403 && responseText.includes('Email not verified')) {
            setFeedbackMessage({ text: 'Email not verified. Redirecting to verification...', type: 'error' });
            setTimeout(() => {
              setShowAuthModal(false);
              if (navigate) {
                navigate('/verify-email', { state: { username } });
              }
            }, 2000);
          } else {
            throw new Error(responseText || 'Login failed');
          }
          return;
        }

        const contentType = response.headers.get('Content-Type');
        let responseData = contentType?.includes('application/json') ? JSON.parse(responseText) : null;

        if (!responseData?.token) {
          throw new Error('No token received in response');
        }

        localStorage.setItem('token', responseData.token);

        const validateResponse = await fetch(`${API_BASE_URL}/api/auth/validate`, {
          headers: { 
            'Authorization': `Bearer ${responseData.token}`,
            'X-Client-IP': clientIp
          },
        });

        const validateText = await validateResponse.text();
        const validateData = validateResponse.headers.get('Content-Type')?.includes('application/json') ? JSON.parse(validateText) : null;

        if (!validateResponse.ok || !validateData) {
          throw new Error(validateText || 'Token validation failed');
        }

        setFeedbackMessage({ text: 'Login successful!', type: 'success' });
        setTimeout(() => {
          setShowAuthModal(false);
          setUsername('');
          setPassword('');
        }, 1000);
      }
    } catch (error) {
      setFeedbackMessage({ text: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComicClick = (title) => {
    // Convert comic title to URL-friendly format (replace spaces with hyphens)
    const urlFriendlyName = title.replace(/\s+/g, '-');
    navigate(`/${urlFriendlyName}`);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans overflow-hidden">
      <Navbar currentPage="Comics" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-black"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(239,68,68,0.15),transparent_50%)]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,51,234,0.1),transparent_50%)]"></div>
            
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(239,68,68,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(239,68,68,0.1)_1px,transparent_1px)] bg-[length:60px_60px]"></div>
            </div>
          </div>

          {/* Section Header */}
          <div className="text-center mb-24 relative z-10">
            <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 sm:py-3 bg-white/5 rounded-full border border-white/10 mb-6 sm:mb-12 backdrop-blur-sm">
              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
              <span className="text-white/80 text-xs sm:text-sm font-medium tracking-wider">COMIC AREA <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs ml-2">TEMP</span></span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black mb-6 tracking-tight bg-gradient-to-r from-red-500 to-purple-500 bg-clip-text text-transparent">
              4Fingerstudios
            </h1>
            <p className="text-xl text-red-400/80 max-w-2xl mx-auto leading-relaxed">
              Explore our collection of comics and stories
            </p>
          </div>
        </div>
      </section>

      {/* Comic Area */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black mb-12 text-center bg-gradient-to-r from-red-500 to-purple-500 bg-clip-text text-transparent">
            Comics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { emoji: '📚', title: 'Comic Title 1', date: 'Coming Soon' },
              { emoji: '🎨', title: 'Comic Title 2', date: 'Coming Soon' },
              { emoji: '🦸', title: 'Comic Title 3', date: 'Coming Soon' }
            ].map((comic, index) => (
              <div
                key={index}
                onClick={() => handleComicClick(comic.title)}
                className="bg-black/80 backdrop-blur-sm border border-red-500/20 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer hover:border-red-500/40 hover:shadow-lg hover:shadow-red-500/20 hover:-translate-y-1"
              >
                <div className="w-full h-96 bg-gradient-to-br from-red-500/10 to-purple-500/10 flex items-center justify-center text-6xl">
                  {comic.emoji}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">{comic.title}</h3>
                  <p className="text-red-400/70 text-sm">Date: {comic.date}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-lg text-white/60">
              Add your comic images and content here!
            </p>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuthModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAuthModal(false)}
        >
          <div className="bg-black/80 backdrop-blur-sm border border-red-500/20 p-8 rounded-2xl shadow-xl hover:border-red-500/40 transition-all duration-300 max-w-md w-full relative">
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors duration-300"
            >
              <X size={24} />
            </button>

            <h2 className="text-3xl font-black mb-8 text-center bg-gradient-to-r from-red-500 to-purple-500 bg-clip-text text-transparent">
              {formState === 'signup' ? 'Sign Up' : 'Login'}
            </h2>

            <form onSubmit={handleAuthSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-white font-medium mb-2">Username</label>
                <Input
                  icon={User}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                />
              </div>

              {formState === 'signup' && (
                <div className="space-y-2">
                  <label className="block text-white font-medium mb-2">Email</label>
                  <Input
                    icon={Mail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-white font-medium mb-2">Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-red-500 to-purple-500 text-white font-bold py-4 rounded-lg hover:from-red-600 hover:to-purple-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-red-500/20"
              >
                {isLoading ? 'Loading...' : (formState === 'signup' ? 'Create Account' : 'Sign In')}
              </button>

              {feedbackMessage.text && (
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  feedbackMessage.type === 'error' 
                    ? 'bg-red-600/20 border border-red-600/30 text-red-400' 
                    : 'bg-green-600/20 border border-green-600/30 text-green-400'
                }`}>
                  <AlertCircle size={18} /> 
                  <span className="text-sm">{feedbackMessage.text}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-between text-sm gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormState(formState === 'signup' ? 'login' : 'signup');
                    setFeedbackMessage({ text: '', type: '' });
                    setUsername('');
                    setEmail('');
                    setPassword('');
                  }}
                  className="text-red-400/70 hover:text-red-300 transition-colors duration-300 font-medium"
                >
                  {formState === 'signup' ? 'Already have an account?' : "Don't have an account?"}
                </button>
                {formState === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAuthModal(false);
                      if (navigate) {
                        navigate('/reset-password');
                      }
                    }}
                    className="text-red-400/70 hover:text-red-300 transition-colors duration-300 font-medium"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComicsPage;

